import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { ConfigProvider } from 'antd';
import type { ThemeConfig } from 'antd';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

import light from './light.json';
import dark from './dark.json';
import type { AppTheme, ThemeTokens } from './types';

type ThemeMode = 'light' | 'dark' | 'system';

interface AppThemeContextValue {
  mode: ThemeMode;
  resolvedMode: 'light' | 'dark';
  setMode: (mode: ThemeMode) => void;
}

const AppThemeContext = createContext<AppThemeContextValue>({
  mode: 'system',
  resolvedMode: 'light',
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  setMode: () => {},
});

const tokenSets: Record<'light' | 'dark', ThemeTokens> = { light, dark };
const STORAGE_KEY = 'rs-theme-mode';

function isValidMode(value: string | null): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

function usePersistedMode(): [ThemeMode, (mode: ThemeMode) => void] {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isValidMode(stored) ? stored : 'light';
  });

  const setMode = useCallback((newMode: ThemeMode) => {
    setModeState(newMode);
    localStorage.setItem(STORAGE_KEY, newMode);
  }, []);

  return [mode, setMode];
}

function useSystemPreference(): 'light' | 'dark' {
  const [preference, setPreference] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') {
      return 'light';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setPreference(e.matches ? 'dark' : 'light');
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    // Fallback for older browsers / jsdom
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }, []);

  return preference;
}

function buildAntTheme(antTokens: Omit<ThemeTokens, 'next'>): ThemeConfig {
  return {
    token: {
      ...antTokens,
      fontFamily: `'Inter', 'Roboto', 'Noto Sans', 'Open Sans', 'Lato', 'Source Sans Pro', sans-serif`,
    },
    components: {
      Button: {
        borderRadius: antTokens.borderRadiusXXL,
        borderRadiusLG: antTokens.borderRadius3XL,
        borderRadiusSM: antTokens.borderRadiusXXL,
        fontSizeLG: antTokens.fontSize,
        // Solid-button labels default to `colorTextLightSolid` (white). The dark
        // palette lightens `colorPrimary`/`colorError` so they read as accents on
        // a dark page, which leaves white labels at 2.84:1 (primary) and 3.01:1
        // (danger) — below the WCAG AA 4.5:1 floor, and worse on hover.
        // The surface color inverts with the theme, so it stays legible on the
        // fill in both: 6.00:1 dark / 5.80:1 light on primary. In light mode
        // `colorBgContainer` is `#fdfeff` — the same value `colorTextLightSolid`
        // already resolves to — so light mode renders identically.
        primaryColor: antTokens.colorBgContainer,
        dangerColor: antTokens.colorBgContainer,
        // Ant gives solid buttons a hard 2px offset shadow
        // (`0 2px 0 <controlOutline>`), a skeuomorphic ledge rather than a soft
        // elevation. On a dark page the primary variant resolves to
        // `rgb(13,27,46)`, which reads as a heavy dark bar welded to the bottom
        // of the button. The design system does not use that ledge, so drop it
        // on all three solid variants in both themes.
        primaryShadow: 'none',
        defaultShadow: 'none',
        dangerShadow: 'none',
      },
      Radio: {
        // A checked solid Radio.Button is filled with `colorPrimary` but keeps
        // Ant's `colorTextLightSolid` (white) label. The dark palette lightens
        // that fill, leaving 2.84:1 — and 2.16:1 on hover, where the fill goes
        // lighter still. `colorBgContainer` inverts with the theme, so it stays
        // legible on the fill in both, and in light mode is `#fdfeff` — the
        // value `colorTextLightSolid` already resolves to — so light is
        // unchanged.
        buttonSolidCheckedColor: antTokens.colorBgContainer,
      },
      Table: {
        borderRadius: 0,
        borderRadiusLG: 0,
      },
      Segmented: {
        colorBgLayout: antTokens.colorFillTertiary,
        itemSelectedBg: antTokens.segmentedBgColorSelected,
      },
      Modal: {
        borderRadiusLG: antTokens.borderRadiusXL,
      },
    },
  };
}

interface AppThemeProviderProps {
  children: ReactNode;
}

export function AppThemeProvider({ children }: AppThemeProviderProps) {
  const [mode, setMode] = usePersistedMode();
  const systemPref = useSystemPreference();
  const resolvedMode = mode === 'system' ? systemPref : mode;

  const tokens = tokenSets[resolvedMode];
  const { next: _next, ...antTokens } = tokens;

  const styledTheme: AppTheme = useMemo(() => ({ token: tokens }), [tokens]);

  const antThemeConfig = useMemo(() => buildAntTheme(antTokens), [tokens]);

  const contextValue = useMemo(
    () => ({ mode, resolvedMode, setMode }),
    [mode, resolvedMode, setMode],
  );

  return (
    <AppThemeContext.Provider value={contextValue}>
      <ConfigProvider theme={antThemeConfig}>
        <StyledThemeProvider theme={styledTheme}>{children}</StyledThemeProvider>
      </ConfigProvider>
    </AppThemeContext.Provider>
  );
}

// ts-prune-ignore-next
export const useAppTheme = () => useContext(AppThemeContext);

// ts-prune-ignore-next — re-exported for buildAntTheme tests
export { buildAntTheme };
